import React, { useEffect, useRef, useState } from "react";
import { Loader2, Upload, Building2, Tag, ListTree, Plus, Trash2, Users, ShieldCheck, FolderCheck, FileText, Pencil, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";
import { api, backendUrl, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { id: "empresa", label: "Empresa & Logo", icon: Building2 },
  { id: "cofre", label: "Documentos & Certidões", icon: FolderCheck },
  { id: "cnaes", label: "CNAEs", icon: ListTree },
  { id: "keywords", label: "Palavras-chave", icon: Tag },
  { id: "usuarios", label: "Usuários", icon: Users },
];

const SICAF = [
  ["", "— Sem nível —"],
  ["I", "I · Credenciamento"],
  ["II", "II · Habilitação Jurídica"],
  ["III", "III · Reg. Fiscal Federal e Trabalhista"],
  ["IV", "IV · Reg. Fiscal Estadual/Municipal"],
  ["V", "V · Qualificação Técnica"],
  ["VI", "VI · Qualificação Econômico-Financeira"],
];
const SICAF_LABEL = Object.fromEntries(SICAF.map(([k, v]) => [k, v]));
const DOC_ST = {
  valido: ["Válido", "bg-emerald-50 text-emerald-700"],
  proximo: ["Vence em breve", "bg-amber-50 text-amber-700"],
  vencido: ["Vencido", "bg-rose-50 text-rose-700"],
  sem_validade: ["Sem validade", "bg-slate-100 text-slate-500"],
};

function Empresa() {
  const [company, setCompany] = useState(null);
  const [logoBust, setLogoBust] = useState(Date.now());
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => { api.get("/company").then((r) => setCompany(r.data)); }, []);

  const save = async () => {
    try {
      await api.put("/company", {
        name: company.name, cnpj: company.cnpj, razao_social: company.razao_social, nome_fantasia: company.nome_fantasia,
        endereco: company.endereco, numero: company.numero, complemento: company.complemento, bairro: company.bairro,
        cidade: company.cidade, uf: company.uf, cep: company.cep, site: company.site,
        telefone: company.telefone, email: company.email, representante_legal: company.representante_legal,
        resp_nome: company.resp_nome, resp_cpf: company.resp_cpf, resp_cargo: company.resp_cargo,
        resp_email: company.resp_email, resp_telefone: company.resp_telefone,
        banco_nome: company.banco_nome, banco_agencia: company.banco_agencia, banco_conta: company.banco_conta,
        banco_pix: company.banco_pix, banco_titular: company.banco_titular,
      });
      toast.success("Dados salvos");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const CF = (k, label, ph, cls = "") => (
    <div className={cls}>
      <label className="mb-1 block text-xs font-semibold text-slate-600">{label}</label>
      <input data-testid={`company-${k}-input`} value={company[k] ?? ""} placeholder={ph || ""}
        onChange={(e) => setCompany({ ...company, [k]: e.target.value })}
        className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
    </div>
  );
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get("/company");
      setCompany(data); setLogoBust(Date.now());
      toast.success("Logo atualizada");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setUploading(false); }
  };

  if (!company) return <Loader2 className="h-6 w-6 animate-spin text-sky-600" />;
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="mb-4 font-display font-semibold text-slate-800">Dados da proponente</h3>
        <p className="mb-4 -mt-2 text-xs text-slate-500">Usados automaticamente no cabeçalho e nas seções de identificação, responsável e assinatura das <strong>propostas comerciais</strong>.</p>
        {CF("razao_social", "Razão social")}
        {CF("nome_fantasia", "Nome fantasia")}
        {CF("cnpj", "CNPJ")}
        {CF("endereco", "Endereço (logradouro)")}
        <div className="grid grid-cols-3 gap-3">
          {CF("numero", "Número")}
          {CF("complemento", "Complemento", "", "col-span-1")}
          {CF("bairro", "Bairro")}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-1">{CF("cidade", "Cidade")}</div>
          {CF("uf", "UF")}
          {CF("cep", "CEP")}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {CF("telefone", "Telefone")}
          {CF("email", "E-mail")}
        </div>
        {CF("site", "Site")}
        {CF("representante_legal", "Representante legal")}
        <div className="my-3 border-t border-slate-100" />
        <h4 className="mb-3 text-sm font-semibold text-slate-700">Responsável pela proposta</h4>
        <div className="grid grid-cols-2 gap-3">
          {CF("resp_nome", "Nome")}
          {CF("resp_cpf", "CPF")}
        </div>
        <div className="grid grid-cols-3 gap-3">
          {CF("resp_cargo", "Cargo/Função")}
          {CF("resp_email", "E-mail", "", "col-span-1")}
          {CF("resp_telefone", "Telefone")}
        </div>
        <div className="my-3 border-t border-slate-100" />
        <h4 className="mb-3 text-sm font-semibold text-slate-700">Dados bancários <span className="text-xs font-normal text-slate-400">(opcional — só aparece na proposta se você ativar)</span></h4>
        <div className="grid grid-cols-2 gap-3">
          {CF("banco_nome", "Banco")}
          {CF("banco_agencia", "Agência")}
          {CF("banco_conta", "Conta")}
          {CF("banco_pix", "PIX")}
        </div>
        {CF("banco_titular", "Titular")}
        <button data-testid="company-save-btn" onClick={save} className="mt-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white">Salvar</button>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="mb-4 font-display font-semibold text-slate-800">Símbolo / Logotipo</h3>
        <div className="flex items-center gap-4">
          <div className="grid h-24 w-24 place-items-center rounded-xl border border-slate-200 bg-slate-50">
            {company.logo_path ? (
              <img src={`${backendUrl}/api/company/logo/raw?t=${logoBust}`} alt="logo" className="h-full w-full rounded-xl object-contain" />
            ) : (
              <ShieldCheck className="h-10 w-10 text-slate-300" />
            )}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="logo-file-input" />
            <button data-testid="logo-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Enviar logo
            </button>
            <p className="mt-2 text-xs text-slate-400">PNG, JPG ou SVG. Aparece no menu lateral.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Cnaes() {
  const [list, setList] = useState(null);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({ code: "", description: "", keywords: "" });
  const [modal, setModal] = useState(null); // {mode:'edit'|'substituir', c}

  const load = () => {
    api.get("/cnaes").then((r) => setList(r.data));
    api.get("/cnae-history").then((r) => setHistory(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.code.trim()) { toast.error("Informe o código do CNAE"); return; }
    try { await api.post("/cnaes", form); setForm({ code: "", description: "", keywords: "" }); load(); toast.success("CNAE adicionado — palavras-chave atualizadas"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (c) => {
    if (!window.confirm(`Excluir o CNAE ${c.code}? As palavras-chave vinculadas também serão removidas.`)) return;
    try { await api.delete(`/cnaes/${c.id}`); load(); toast.success("CNAE excluído"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const saveModal = async () => {
    try {
      if (modal.mode === "edit") await api.put(`/cnaes/${modal.c.id}`, { code: modal.code, description: modal.description, keywords: modal.keywords });
      else await api.post(`/cnaes/${modal.c.id}/substituir`, { code: modal.code, description: modal.description, keywords: modal.keywords });
      setModal(null); load(); toast.success(modal.mode === "edit" ? "CNAE atualizado" : "CNAE substituído — palavras-chave atualizadas");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!list) return <Loader2 className="h-6 w-6 animate-spin text-sky-600" />;
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <p className="mb-3 text-sm text-slate-500">CNAEs cadastrados da PRO INVICTA — base da análise de aderência (Radar, filtros, compatibilidade de itens e sugestões). Adicionar/substituir atualiza automaticamente as palavras-chave relacionadas.</p>
        <div className="mb-4 grid gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3 sm:grid-cols-[140px_1fr_1fr_auto]">
          <input data-testid="cnae-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Código (0000-0/00)" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <input data-testid="cnae-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Descrição" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <input data-testid="cnae-kw-input" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="Palavras-chave (vírgula) — opcional" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <button data-testid="cnae-add-btn" onClick={add} className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Adicionar</button>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          {list.map((c) => (
            <div key={c.id} data-testid="cnae-item" className="flex items-center gap-2 rounded-lg border border-slate-100 p-3">
              <span className="rounded bg-slate-900 px-2 py-1 font-mono text-xs text-white">{c.code}</span>
              <span className="flex-1 text-sm text-slate-700">{c.description}</span>
              {c.principal && <span className="rounded bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Principal</span>}
              <button data-testid="cnae-edit-btn" title="Editar" onClick={() => setModal({ mode: "edit", c, code: c.code, description: c.description, keywords: "" })} className="rounded p-1 text-slate-400 hover:text-sky-600"><Pencil className="h-4 w-4" /></button>
              <button data-testid="cnae-substituir-btn" title="Substituir" onClick={() => setModal({ mode: "substituir", c, code: "", description: "", keywords: "" })} className="rounded p-1 text-slate-400 hover:text-amber-600"><RefreshCw className="h-4 w-4" /></button>
              <button data-testid="cnae-delete-btn" title="Excluir" onClick={() => del(c)} className="rounded p-1 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>

      {history.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="cnae-history">
          <h4 className="mb-2 text-sm font-semibold text-slate-700">Histórico de alterações</h4>
          <ul className="space-y-1.5">
            {history.map((h) => (
              <li key={h.id} className="flex items-start gap-2 text-xs text-slate-600">
                <span className="mt-0.5 rounded bg-slate-100 px-1.5 py-0.5 font-semibold uppercase text-slate-500">{h.action}</span>
                <span className="flex-1">
                  {h.action === "substituir" && h.removed ? <><span className="text-rose-600">{h.removed}</span> → </> : null}
                  <span className="text-emerald-700">{h.code}{h.description ? ` — ${h.description}` : ""}</span>
                  {typeof h.removed_keywords === "number" && h.removed_keywords > 0 ? ` · −${h.removed_keywords} palavra(s)-chave` : ""}
                  {Array.isArray(h.added_keywords) && h.added_keywords.length ? ` · +${h.added_keywords.length} palavra(s)-chave` : ""}
                  <span className="text-slate-400"> · {fmtDateTime(h.at)} · {h.user}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setModal(null)} />
          <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl" data-testid="cnae-modal">
            <h3 className="mb-1 font-display text-lg font-bold">{modal.mode === "edit" ? "Editar CNAE" : "Substituir CNAE"}</h3>
            {modal.mode === "substituir" && <p className="mb-3 text-xs text-slate-500">Substituindo <strong>{modal.c.code}</strong>. O CNAE atual e suas palavras-chave serão removidos; o novo será cadastrado com palavras-chave atualizadas.</p>}
            <label className="mb-1 block text-xs font-semibold text-slate-600">Código</label>
            <input data-testid="cnae-modal-code" value={modal.code} onChange={(e) => setModal({ ...modal, code: e.target.value })} placeholder="0000-0/00" className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <label className="mb-1 block text-xs font-semibold text-slate-600">Descrição</label>
            <input data-testid="cnae-modal-desc" value={modal.description} onChange={(e) => setModal({ ...modal, description: e.target.value })} className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <label className="mb-1 block text-xs font-semibold text-slate-600">Palavras-chave adicionais (vírgula)</label>
            <input data-testid="cnae-modal-kw" value={modal.keywords} onChange={(e) => setModal({ ...modal, keywords: e.target.value })} placeholder="opcional" className="mb-4 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancelar</button>
              <button data-testid="cnae-modal-save" onClick={saveModal} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white">Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Keywords() {
  const [list, setList] = useState(null);
  const [term, setTerm] = useState("");
  const [type, setType] = useState("positive");

  const load = () => api.get("/keywords").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!term.trim()) return;
    try { await api.post("/keywords", { term: term.trim(), type }); setTerm(""); load(); toast.success("Palavra-chave adicionada"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { try { await api.delete(`/keywords/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };

  if (!list) return <Loader2 className="h-6 w-6 animate-spin text-sky-600" />;
  const pos = list.filter((k) => k.type === "positive");
  const neg = list.filter((k) => k.type === "negative");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <input data-testid="keyword-input" value={term} onChange={(e) => setTerm(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Nova palavra-chave" className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
          <option value="positive">Positiva</option>
          <option value="negative">Negativa</option>
        </select>
        <button data-testid="keyword-add-btn" onClick={add} className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Adicionar</button>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h4 className="mb-3 text-sm font-semibold text-emerald-700">Positivas ({pos.length})</h4>
          <div className="flex flex-wrap gap-1.5">
            {pos.map((k) => (
              <span key={k.id} className="group flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                {k.term}
                <button onClick={() => del(k.id)} className="opacity-0 transition-opacity group-hover:opacity-100"><Trash2 className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h4 className="mb-3 text-sm font-semibold text-rose-700">Negativas ({neg.length})</h4>
          <div className="flex flex-wrap gap-1.5">
            {neg.map((k) => (
              <span key={k.id} className="group flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700">
                {k.term}
                <button onClick={() => del(k.id)} className="opacity-0 transition-opacity group-hover:opacity-100"><Trash2 className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function UsersTab() {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/auth/users").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  if (!list) return <Loader2 className="h-6 w-6 animate-spin text-sky-600" />;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 grid grid-cols-4 gap-2 text-xs font-semibold uppercase text-slate-400">
        <span className="col-span-2">Usuário</span><span>Perfil</span><span>Origem</span>
      </div>
      {list.map((u) => (
        <div key={u.user_id} className="grid grid-cols-4 items-center gap-2 border-t border-slate-100 py-2.5 text-sm">
          <div className="col-span-2">
            <div className="font-medium text-slate-800">{u.name}</div>
            <div className="text-xs text-slate-400">{u.email}</div>
          </div>
          <span className="capitalize"><span className="rounded bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700">{u.role}</span></span>
          <span className="text-xs capitalize text-slate-500">{u.provider}</span>
        </div>
      ))}
    </div>
  );
}

function DocumentosCofre() {
  const [list, setList] = useState(null);
  const [modal, setModal] = useState(null);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/company-documents").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!modal.nome) { toast.error("Informe o nome do documento"); return; }
    setSaving(true);
    try {
      const body = { ...modal };
      let id = modal.id;
      if (id) await api.put(`/company-documents/${id}`, body);
      else { const { data } = await api.post("/company-documents", body); id = data.id; }
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/company-documents/${id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Documento salvo");
      setModal(null); setFile(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const del = async (id) => { try { await api.delete(`/company-documents/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };

  if (!list) return <Loader2 className="h-6 w-6 animate-spin text-sky-600" />;

  const grupos = SICAF.filter(([k]) => k).map(([k, label]) => ({
    nivel: k, label, docs: list.filter((d) => d.sicaf_nivel === k),
  }));
  const semNivel = list.filter((d) => !d.sicaf_nivel);
  const vencidos = list.filter((d) => d.status === "vencido").length;
  const proximos = list.filter((d) => d.status === "proximo").length;

  return (
    <div className="space-y-4" data-testid="cofre-documental">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-slate-500">Repositório central de documentos e certidões da PRO INVICTA, organizado pelos níveis do SICAF. Reutilizado em todas as licitações e cruzado com a Matriz de Habilitação.</p>
        <button data-testid="cofre-add-btn" onClick={() => { setModal({}); setFile(null); }} className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700">
          <Plus className="h-4 w-4" /> Novo documento
        </button>
      </div>
      {(vencidos > 0 || proximos > 0) && (
        <div className="flex flex-wrap gap-2 text-xs font-semibold">
          {vencidos > 0 && <span className="rounded-lg bg-rose-50 px-3 py-1.5 text-rose-700">🔴 {vencidos} vencido(s)</span>}
          {proximos > 0 && <span className="rounded-lg bg-amber-50 px-3 py-1.5 text-amber-700">🟡 {proximos} vence(m) em ≤30 dias</span>}
        </div>
      )}

      {list.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">Nenhum documento cadastrado ainda.</div>
      )}

      {[...grupos, ...(semNivel.length ? [{ nivel: "", label: "Sem nível SICAF", docs: semNivel }] : [])].map((g) => (
        g.docs.length === 0 ? null : (
          <div key={g.nivel || "none"} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-sky-600" />
              <h4 className="text-sm font-semibold text-slate-800">{g.label}</h4>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">{g.docs.length}</span>
            </div>
            <div className="space-y-2">
              {g.docs.map((d) => {
                const [stl, stcls] = DOC_ST[d.status] || DOC_ST.sem_validade;
                return (
                  <div key={d.id} data-testid="cofre-doc-row" className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-100 p-3">
                    <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-800">{d.nome}</div>
                      <div className="text-xs text-slate-400">{d.categoria || "—"}{d.numero ? ` · nº ${d.numero}` : ""}{d.data_validade ? ` · validade ${new Date(d.data_validade).toLocaleDateString("pt-BR")}` : ""}</div>
                    </div>
                    <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${stcls}`}>{stl}</span>
                    {d.file_name && <a href={`${backendUrl}/api/company-documents/${d.id}/file`} target="_blank" rel="noreferrer" className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-sky-600"><Download className="h-4 w-4" /></a>}
                    <button onClick={() => { setModal(d); setFile(null); }} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-sky-600"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del(d.id)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                );
              })}
            </div>
          </div>
        )
      ))}

      {modal && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setModal(null)} />
          <div className="relative w-full max-w-xl rounded-xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center gap-2"><FolderCheck className="h-5 w-5 text-sky-600" /><h3 className="font-display text-lg font-bold">{modal.id ? "Editar documento" : "Novo documento"}</h3></div>
            <div className="grid max-h-[65vh] grid-cols-2 gap-3 overflow-y-auto">
              <div className="col-span-2">
                <label className="mb-1 block text-xs font-semibold text-slate-600">Nome do documento *</label>
                <input data-testid="cofre-input-nome" value={modal.nome ?? ""} onChange={(e) => setModal({ ...modal, nome: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Categoria</label>
                <input value={modal.categoria ?? ""} onChange={(e) => setModal({ ...modal, categoria: e.target.value })} placeholder="Certidão, Atestado..." className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Nível SICAF</label>
                <select data-testid="cofre-input-sicaf" value={modal.sicaf_nivel ?? ""} onChange={(e) => setModal({ ...modal, sicaf_nivel: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
                  {SICAF.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Número do documento</label>
                <input value={modal.numero ?? ""} onChange={(e) => setModal({ ...modal, numero: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Órgão emissor</label>
                <input value={modal.orgao_emissor ?? ""} onChange={(e) => setModal({ ...modal, orgao_emissor: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Data de emissão</label>
                <input type="date" value={modal.data_emissao ?? ""} onChange={(e) => setModal({ ...modal, data_emissao: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Data de validade</label>
                <input data-testid="cofre-input-validade" type="date" value={modal.data_validade ?? ""} onChange={(e) => setModal({ ...modal, data_validade: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <div className="col-span-2">
                <label className="mb-1 block text-xs font-semibold text-slate-600">Observação</label>
                <textarea value={modal.observacao ?? ""} onChange={(e) => setModal({ ...modal, observacao: e.target.value })} rows={2} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <div className="col-span-2">
                <label className="mb-1 block text-xs font-semibold text-slate-600">Arquivo {modal.file_name ? `(atual: ${modal.file_name})` : ""}</label>
                <input data-testid="cofre-input-file" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-semibold" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancelar</button>
              <button data-testid="cofre-save-btn" onClick={save} disabled={saving} className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Configuracoes() {
  const [tab, setTab] = useState("empresa");
  return (
    <div className="animate-fade-up">
      <div className="mb-4">
        <h1 className="font-display text-2xl font-bold text-slate-900">Configurações</h1>
        <p className="text-sm text-slate-500">Empresa, CNAEs, palavras-chave e usuários.</p>
      </div>
      <div className="mb-5 flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1 text-sm font-medium">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`settings-tab-${t.id}`} onClick={() => setTab(t.id)} className={`flex items-center gap-2 rounded-md px-4 py-1.5 ${tab === t.id ? "bg-white text-slate-900 shadow" : "text-slate-500"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "empresa" && <Empresa />}
      {tab === "cofre" && <DocumentosCofre />}
      {tab === "cnaes" && <Cnaes />}
      {tab === "keywords" && <Keywords />}
      {tab === "usuarios" && <UsersTab />}
    </div>
  );
}
